import assert from 'assert'
import React, { Children, isValidElement } from 'react'
import { Tooltip } from '../../components/simple/tooltip'
import { useLocation, useNavigate } from 'react-router-dom'

type TabProps = {
  /** The name of the tab. This is used to identify the tab in the URL hash. */
  tab: string
  /** The label of the tab. This is what is displayed to the user. */
  label: React.ReactNode
  /** optional tooltip shown on the tab butt */
  tooltip?: string
  /** Should this tab be disabled and unselectable */
  disabled?: boolean
  /** The content of the tab. This is displayed when the tab is selected. */
  children: React.ReactNode
  /** Controlled by <Tabs/>: Whether the tab is currently the active tab. You shouldn't need to set this manually. */
  active?: boolean
  /** Used by <Tabs/>: When the tab is clicked, this function is called. You shouldn't need to set this manually. */
  onClick?: () => void
}

/**
 * A component that displays a set of tabs and allows the user to switch between them.
 * It uses the hash in the URL to determine which tab is currently selected.
 * If the hash is not present, it defaults to the first tab.
 *
 * Use the `Tab` component to define each tab. Only `Tab` components are accepted as children.
 */
export const Tabs: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => {
  const flattenChildren = (children: React.ReactNode): React.ReactNode[] => {
    return Children.toArray(children).flatMap((child) =>
      isValidElement(child) && child.type === React.Fragment ? flattenChildren(child.props.children) : [child]
    )
  }
  const childrenList = flattenChildren(children)
  const tabs = childrenList.filter(
    (child) => isValidElement(child) && child.type === Tab
  ) as React.ReactElement<TabProps>[]
  const tabNames = tabs.filter((tab) => !tab.props.disabled).map((tab) => tab.props.tab)
  const [selectedTab, setSelectedTab] = useHashTab(tabNames[0], tabNames)
  React.useEffect(() => {
    if (!tabNames.includes(selectedTab)) setSelectedTab(tabNames[0])
  }, [tabNames, selectedTab])
  const selectedTabElement = tabs.find((tab) => tab.props.tab === selectedTab)
  assert(tabs.length === childrenList.length, 'Tabs component only accepts Tab children')
  return (
    <>
      <div className={`flex border-b border-gray-100 ${className}`}>
        {tabs.map((tab) =>
          React.cloneElement(tab, {
            key: tab.props.tab,
            active: tab.props.tab === selectedTab,
            onClick: () => setSelectedTab(tab.props.tab),
          })
        )}
      </div>
      {selectedTabElement?.props.children}
    </>
  )
}

/**
 * A component that represents a single tab in the Tabs component.
 */
export const Tab: React.FC<TabProps> = ({ label, active, onClick, tooltip, disabled }) => (
  <Tooltip text={tooltip}>
    <div
      className={`select-none px-5 py-2 font-bold ${active ? 'bg-gray-50 text-neutral-900' : 'bg-transparent text-neutral-400'} ${
        disabled ? 'opacity-50' : 'cursor-pointer'
      }`}
      onClick={disabled ? undefined : onClick}
    >
      {label}
    </div>
  </Tooltip>
)

export const useHashTab = (initial: string, options?: string[]) => {
  const location = useLocation()
  const navigate = useNavigate()

  const hash = location.hash.replace(/^#/, '').trim()
  const cleanHash = options?.includes(hash) ? hash : initial

  const [tab, setTab] = React.useState(cleanHash)

  React.useEffect(() => {
    const currentHash = location.hash.replace(/^#/, '').trim()
    if (currentHash !== tab) {
      const searchParams = location.search // Preserve existing query parameters
      navigate(`${location.pathname}${searchParams}#${tab}`, { replace: true })
    }
  }, [tab])

  React.useEffect(() => {
    if (cleanHash !== tab && cleanHash) setTab(hash)
  }, [cleanHash])

  return [tab, setTab] as const
}
